import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import TabsV2, {
    TabsActivation,
    TabV2Props,
} from 'flow/components/pbx/v2/misc/TabsV2';

interface Props {
    tabs: TabV2Props[];
    selectedKey?: string | number;
    onClick: (tab: TabV2Props) => void;
    /** Optional element rendered after the pills (e.g. a dock toggle). */
    trailing?: React.ReactNode;
    /**
     * Stretch the pills to fill the row width when they don't overflow -
     * useful in narrow side panels where a few pills would otherwise leave
     * empty space. Falls back to natural widths + scrolling when crowded.
     */
    stretch?: boolean;
    /** See `TabsV2`. */
    activation?: TabsActivation;
    /** See `TabsV2`. */
    panelId?: string;
}

// Fade only the edge(s) that actually have content scrolled past them,
// so a non-scrolling row keeps its first/last pill fully visible.
const edgeMask = (fadeLeft: boolean, fadeRight: boolean) => {
    const left = fadeLeft ? 'transparent 0, black 16px' : 'black 0';
    const right = fadeRight
        ? 'black calc(100% - 16px), transparent 100%'
        : 'black 100%';
    return `linear-gradient(to right, ${left}, ${right})`;
};

// Round overflow chevron, pinned to the inline start/end edge. `rtl:` flips
// the glyph so it always points the way it scrolls. It is for the pointer
// only: the arrow keys reach every pill and scroll it into view, so the
// chevron is not a Tab stop, screen readers skip it, and a click on it
// leaves focus where it was.
const ScrollChevron = ({
    side,
    iconKey,
    label,
    visible,
    onClick,
}: {
    side: 'start' | 'end';
    iconKey: IconKey;
    label: string;
    visible: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        tabIndex={-1}
        aria-hidden={true}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
        aria-label={label}
        className={twMerge(
            'hover:darken-5 absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/50 text-gray-800 shadow-md backdrop-blur-sm transition-opacity duration-200 ease-out',
            side === 'start' ? 'start-2' : 'end-2',
            visible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
    >
        <Icon iconKey={iconKey} size="1.25rem" className="rtl:scale-x-[-1]" />
    </button>
);

/**
 * Horizontally-scrollable row of `TabsV2` pills with drag-to-scroll,
 * wheel->horizontal, edge fades and overflow chevrons - the shared
 * "pills menu" behaviour used by the ticket tabs, call-record detail
 * tabs and the call history filter.
 */
const ScrollablePillTabs = ({
    tabs,
    selectedKey,
    onClick,
    trailing,
    stretch = false,
    activation,
    panelId,
}: Props) => {
    const { i18next } = useAppContext();
    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragScrollLeft = useRef(0);
    // Fade state — tracks whether content is actually clipped on a side, so
    // the edge is never a hard cut.
    const [canScrollToStart, setCanScrollToStart] = useState(false);
    const [canScrollToEnd, setCanScrollToEnd] = useState(false);
    // Chevron state — only once a pill is clipped clearly past the fade.
    const [showStartChevron, setShowStartChevron] = useState(false);
    const [showEndChevron, setShowEndChevron] = useState(false);
    // The row's inline padding (`px-3`), read once per resize rather than
    // through getComputedStyle on every scroll event.
    const padding = useRef({ start: 0, end: 0 });
    // Writing direction, from the same source as the edge fade below so the
    // wheel, the chevrons and the fade always agree; a language change
    // re-renders this component, which keeps the ref the wheel reads current.
    const isRtl = i18next.dir() === 'rtl';
    const isRtlRef = useRef(isRtl);
    useEffect(() => {
        isRtlRef.current = isRtl;
    }, [isRtl]);

    const smoothScrollTo = useCallback((left: number) => {
        scrollRef.current?.scrollTo({ left, behavior: 'smooth' });
    }, []);

    const updateOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Chevrons appear only once a pill is clipped clearly past the edge
        // fade (16px) — not while it's a sliver short.
        const EDGE = 16;
        // Tiny threshold so the edge fade appears as soon as the row is
        // scrolled on a side — i.e. as a pill starts moving under the edge,
        // not only once it has cleared the row's start/end padding.
        const FADE = 2;
        // Padding on each side scrolls past before a pill is actually clipped;
        // fold it into the chevron threshold so the chevron keys off real pill
        // clipping (and a selected last pill can scroll clear of its padding
        // rather than sitting flush against the panel edge).
        const { start: padStart, end: padEnd } = padding.current;
        // `scrollLeft` is 0 at the start edge and goes negative when
        // scrolled in RTL (the model all modern browsers use), so its
        // absolute value is a direction-agnostic distance from the start.
        const scrolled = Math.abs(el.scrollLeft);
        const maxScroll = el.scrollWidth - el.clientWidth;
        const fromEnd = maxScroll - scrolled;
        // Fade as soon as the row is scrolled on a side, so the edge softens
        // before a pill is clipped rather than after it clears the padding.
        setCanScrollToStart(scrolled > FADE);
        setCanScrollToEnd(fromEnd > FADE);
        // Chevrons wait for a clear overflow past the fade.
        setShowStartChevron(scrolled > padStart + EDGE);
        setShowEndChevron(fromEnd > padEnd + EDGE);
    }, []);

    // A layout change is the only time the padding can change, so the
    // ResizeObserver reads it; the scroll listener only does the maths.
    const measurePaddingAndUpdate = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const styles = getComputedStyle(el);
        padding.current = {
            start: parseFloat(styles.paddingInlineStart) || 0,
            end: parseFloat(styles.paddingInlineEnd) || 0,
        };
        updateOverflow();
    }, [updateOverflow]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const onWheel = (e: WheelEvent) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            // Wheel-down should advance toward the end, which is leftward
            // (a decreasing scrollLeft) in RTL.
            const direction = isRtlRef.current ? -1 : 1;
            el.scrollLeft += e.deltaY * 0.8 * direction;
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('scroll', updateOverflow, { passive: true });
        const ro = new ResizeObserver(measurePaddingAndUpdate);
        ro.observe(el);
        // Also watch the inner row: the container is full-width and never
        // resizes, so only the content shrinking/growing (e.g. the temporary
        // Filter pill unmounting) tells us overflow has changed.
        if (contentRef.current) ro.observe(contentRef.current);
        measurePaddingAndUpdate();
        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('scroll', updateOverflow);
            ro.disconnect();
        };
    }, [measurePaddingAndUpdate, updateOverflow]);

    useEffect(() => {
        updateOverflow();
    }, [tabs, updateOverflow]);

    // Scroll a pill into view, keeping clear of the chevrons and edge fades.
    // Worked out in physical (screen) space so it holds for both LTR and
    // RTL: nudging the content by a `scrollLeft` delta is the same in
    // either writing direction.
    const scrollIntoSafeZone = useCallback(
        (node: Element) => {
            const el = scrollRef.current;
            if (!el) return;
            const ARROW_SAFE_ZONE = 48;
            const containerRect = el.getBoundingClientRect();
            const nodeRect = node.getBoundingClientRect();
            const leftBound = containerRect.left + ARROW_SAFE_ZONE;
            const rightBound = containerRect.right - ARROW_SAFE_ZONE;
            if (nodeRect.left < leftBound) {
                smoothScrollTo(el.scrollLeft - (leftBound - nodeRect.left));
            } else if (nodeRect.right > rightBound) {
                smoothScrollTo(el.scrollLeft + (nodeRect.right - rightBound));
            }
        },
        [smoothScrollTo],
    );

    useEffect(() => {
        const node = scrollRef.current?.querySelector('[aria-selected="true"]');
        if (node) scrollIntoSafeZone(node);
    }, [selectedKey, scrollIntoSafeZone]);

    // A pill that gets keyboard focus must not stay under a chevron or fade
    // (WCAG 2.4.11). Focus from a press is left alone, so it cannot fight a
    // drag that starts on a pill.
    const onFocus = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            if (!isDragging.current) scrollIntoSafeZone(e.target);
        },
        [scrollIntoSafeZone],
    );

    const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        isDragging.current = true;
        dragStartX.current = e.clientX;
        dragScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    }, []);
    const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging.current || !scrollRef.current) return;
        e.preventDefault();
        const dx = e.clientX - dragStartX.current;
        scrollRef.current.scrollLeft = dragScrollLeft.current - dx;
    }, []);
    const stopDrag = useCallback(() => {
        isDragging.current = false;
    }, []);
    const scrollToStart = useCallback(
        () => smoothScrollTo(0),
        [smoothScrollTo],
    );
    const scrollToEnd = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // The end edge sits at a negative scrollLeft in RTL.
        const maxScroll = el.scrollWidth - el.clientWidth;
        smoothScrollTo(isRtl ? -maxScroll : maxScroll);
    }, [smoothScrollTo, isRtl]);

    // The fade belongs on whichever physical edge hides content. In RTL
    // the start sits on the right, so the start/end -> left/right mapping
    // flips.
    const mask = edgeMask(
        isRtl ? canScrollToEnd : canScrollToStart,
        isRtl ? canScrollToStart : canScrollToEnd,
    );
    // Only offer the drag-to-scroll grab cursor when the row actually
    // overflows - otherwise the empty space beside the pills looks
    // grabbable when there's nothing to scroll.
    const isScrollable = canScrollToStart || canScrollToEnd;

    // The scroller clips vertically too, so `py-1` makes room for a pill's
    // focus ring and count badge, and `-my-1` gives the row its old height.
    const scrollRow = (
        <div // NOSONAR
            // Sonar reads the handlers as a sign that this div is a control.
            // It is not a control, and it never gets focus. The focus handler
            // scrolls the pill that has focus into view, and the mouse
            // handlers add a drag for pointer users. The arrow keys already
            // reach every pill, so a role and a Tab stop would add nothing.
            ref={scrollRef}
            className={twMerge(
                // Symmetric padding gives the first/last pill breathing room
                // (and lets a selected last pill scroll clear of the edge); the
                // overflow maths above subtract this padding so the fade and
                // chevrons still key off real pill clipping, not the padding.
                'hide-scrollbar -my-1 flex items-center overflow-x-auto px-3 py-1',
                isScrollable && 'cursor-grab active:cursor-grabbing',
            )}
            style={{ maskImage: mask, WebkitMaskImage: mask }}
            onFocus={onFocus}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
        >
            <div
                ref={contentRef}
                className={twMerge(
                    'flex items-center gap-2',
                    stretch ? 'min-w-full' : 'shrink-0',
                )}
            >
                <TabsV2
                    className={stretch ? 'grow' : 'shrink-0'}
                    stretch={stretch}
                    selectedKey={selectedKey}
                    tabs={tabs}
                    onClick={onClick}
                    activation={activation}
                    panelId={panelId}
                />
                {trailing}
            </div>
        </div>
    );

    return (
        <div className="relative w-full shrink-0">
            {scrollRow}

            <ScrollChevron
                side="start"
                iconKey="chevron_left"
                label={i18next.t(
                    'tickets.pills.scrollStart',
                    'Scroll to start',
                )}
                visible={showStartChevron}
                onClick={scrollToStart}
            />
            <ScrollChevron
                side="end"
                iconKey="chevron_right"
                label={i18next.t('tickets.pills.scrollEnd', 'Scroll to end')}
                visible={showEndChevron}
                onClick={scrollToEnd}
            />
        </div>
    );
};

export default ScrollablePillTabs;
